import React, { useState } from 'react';
import { getBetIconClass } from '../../../utils';
import PropTypes from 'prop-types';
import './BetNg.less';
import { SportIcon } from '@/nikeone/components/MiniScoreboardPrematch';
import { formatOdds, formatTicketDetailDate } from '@/common/client';
import i18n from '@i18n';
import TicketScoreboardNg, { virtualStartedBet } from './TicketScoreboardNg';
import { dispatch, useReframe } from 'nike-re-framejs';
import ArrowDownIcon from '@/icons/ArrowDownIcon';
import ArrowRightInCircleIcon from '@/icons/ArrowRightInCircleIcon';
import { denormalizeSportEventId } from '@/nikeone/domain/sportevent';
import moment from 'moment';
import PopupNg from '@/ui/PopupNg';
import clsx from 'clsx';
import Nbsp from '@/common/component/Nbsp';
import { COMMUNITY_NEW_ANALYSIS_FROM_TICKET, NIKE1_MATCH, TURBO_LIVE_MATCH_DETAIL } from '@/routing/routes';
import { navigate } from '@/routing';
import { betSportEventUrlDescription } from 'nike-ui/src/common/sportEvents';
import CreateAnalysisIcon from 'nike-ui/src/icons/CreateAnalysisIcon';
import CashinClosingModeExpandRow from 'nike-ui/src/components/ticketus/cashin/CashinClosingModeExpandRow';
import { cashinClosingModeActive$, newTransactionStarted$ } from 'nike-ui/src/components/ticketus/subs';
import { clearCashinClosingMode } from 'nike-ui/src/components/ticketus/handlers';
import CashinClosingModeClosedBet from 'nike-ui/src/components/ticketus/cashin/CashinClosingModeClosedBet';
import BetWonIcon from 'nike-ui/src/icons/BetWonIcon';
import BetLostIcon from 'nike-ui/src/icons/BetLostIcon';
import BetRegisteredIcon from 'nike-ui/src/icons/BetRegisteredIcon';
import { getClassNameByBet, getSportNameById } from 'nike-ui/src/common/utils/utils';
import { cashinOfferByTicketId$$ } from 'nike-ui/src/components/turboLive/components/cashin/subs';
import { useDarkTheme } from 'nike-ui/src/common/theme';
import InfoIconWithModal from '@/mytickets/detail/views/Actions/InfoIconWithModal';

function getCashinPickData(cashinOffer, betId) {
    return cashinOffer?.picks.find(pick => +pick.betId === betId) || {};
}

function renderBetIcon(bet) {
    const status = bet.get('status');
    if (status === 'Registered') {
        return <BetRegisteredIcon closedBet={bet.get('closeOdds')} />;
    }
    if (status === 'Won') {
        return <BetWonIcon closedBet={bet.get('closeOdds')} />;
    }
    if (status === 'Lost') {
        return <BetLostIcon closedBet={bet.get('closeOdds')} />;
    }
    return <div className={getBetIconClass(bet)} />;
}

function TicketStateIcon({ bet, isExpanded }) {
    return (
        <div className="bet-header-status-icon">
            <div className={clsx('ticket-state-badge status-badge', getClassNameByBet(bet))} />
            <div className="ticket-detail-ticket-state">{renderBetIcon(bet)}</div>
            <div className={clsx('scoreboard-accordion-icon', { down: isExpanded })}>
                <ArrowDownIcon fill={!virtualStartedBet(bet) ? '#5D9CEC' : '#949494'} width={20} height={12} />
            </div>
        </div>
    );
}

function PickHeader({ bet, ticketId, createAnalysisBetIds, source, currentUserTicket }) {
    const { subscribe } = useReframe();
    const home = bet.getIn(['opponents', 0]);
    const away = bet.getIn(['opponents', 1]);
    const betId = bet.get('betId');
    const sportId = bet.get('sportId');
    const sportName = getSportNameById(sportId);
    const type = bet.get('betType');
    const isLiveBet = type && type === 'Live';
    const urlMeta = betSportEventUrlDescription(bet);
    const cashinClosingModeActive = subscribe(cashinClosingModeActive$(ticketId));
    const createAnalysisAvailable = createAnalysisBetIds?.some(item => item === betId) && !cashinClosingModeActive;
    const savedTicket = source === 'Saved';

    const goToMatchDetail = e => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(clearCashinClosingMode);
        if (isLiveBet) {
            navigate(TURBO_LIVE_MATCH_DETAIL, {
                sportName: sportName,
                matchId: denormalizeSportEventId(bet.get('betEventId'))
            });
        } else {
            navigate(NIKE1_MATCH, { eventId: denormalizeSportEventId(bet.get('betEventId')), meta: urlMeta });
        }
        PopupNg.dispatcher.close();
    };

    const handleCreateAnalysisClick = e => {
        e.stopPropagation();
        dispatch(clearCashinClosingMode);
        navigate(COMMUNITY_NEW_ANALYSIS_FROM_TICKET, { betId: betId, ticketId: ticketId });
        PopupNg.dispatcher.close('betbook/ticketDetailPopup');
    };

    const renderButtonsOnPick = reason => {
        const showArrowIcon =
            type !== 'Virtual' &&
            !cashinClosingModeActive &&
            moment().diff(moment(bet.get('expirationDate')), 'days') < 7;
        return (
            <>
                {currentUserTicket ? <InfoIconWithModal reason={reason} showArrowIcon={showArrowIcon} /> : null}
                {showArrowIcon ? (
                    <button className="match-detail-icon" onClick={goToMatchDetail} data-atid="ticket-match-detail">
                        <ArrowRightInCircleIcon />
                    </button>
                ) : null}
            </>
        );
    };

    return (
        <div className="ticket-detail-bet">
            <div className="ticket-detail-bet-row ticket-detail-bet-opponents">
                <SportIcon sportEvent={bet} />
                <div className={away ? 'home' : 'home home-only'}>{home}</div>
                {away ? (
                    <div className="opponents-separator">{bet.get('score') || i18n('common.separator')}</div>
                ) : null}
                {away ? (
                    <div data-atid="super-away" className="away">
                        {away}
                    </div>
                ) : null}
            </div>
            <div className="ticket-detail-bet-row ticket-detail-bet-market">
                {bet.get('betHeaderDetail')}
                {bet.get('players') && bet.get('players').size ? (
                    <span>: {bet.get('players').join(' - ')}</span>
                ) : null}{' '}
                - {bet.get('pickName')}
            </div>

            <div className="ticket-detail-bet-row ticket-detail-bet-tournament">
                {bet.get('betNumber')} - {bet.get('tournament')}
            </div>

            <div className="ticket-detail-bet-row ticket-detail-bet-other-info">
                {isLiveBet && <div className="event-flag Live">{i18n('event.flag.live')}</div>}
                {formatTicketDetailDate(bet.get('expirationDate'))}
                <div className="bet-odds" data-atid="bet-odds">
                    {i18n('jsp.detailTiketu.kurz')}
                    <Nbsp />
                    <span className="odd-value">{formatOdds(bet.get('odds'))}</span>
                </div>
            </div>
            {renderButtonsOnPick(bet.get('returnReason'))}
            {createAnalysisAvailable && !savedTicket ? (
                <div className="ticket-detail-bet-row ticket-detail-bet-create-analysis">
                    <button className="flex items-center" onClick={handleCreateAnalysisClick}>
                        <CreateAnalysisIcon />
                        <div className="create-analysis-label">{i18n('betbook.ticket-detail.create-analysis')}</div>
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function BetNg({
    bet,
    ticketId,
    createAnalysisBetIds,
    source,
    handleCashinClosingClick,
    handleToggleDisabledPick,
    currentUserTicket
}) {
    const { derefSub, subscribe } = useReframe();
    const isExpanded = derefSub(['ticketus/scoreboard/is-open', bet.get('betEventId')]);
    const [openVirtual, setOpenVirtual] = useState();
    const cashinClosingModeActive = subscribe(cashinClosingModeActive$(ticketId));
    const isDarkTheme = useDarkTheme();

    const currentOffer = subscribe(cashinOfferByTicketId$$(ticketId));
    const newTransactionStarted = subscribe(newTransactionStarted$);
    const { currentCashinOdds, pickState } = getCashinPickData(currentOffer, bet.get('betId'));
    const openScoreboard = () => {
        if (bet.get('betType') === 'Virtual') {
            setOpenVirtual(open => !open);
            return;
        }
        if (isExpanded) {
            dispatch(['ticketus/scoreboard/remove-match', bet.get('betEventId')]);
        } else {
            dispatch(['ticketus/scoreboard/add-match', bet.get('betEventId')]);
        }
    };

    return (
        <div
            className="ticket-detail-bet-wrap"
            data-sport={bet.get('sport')}
            data-tournament={bet.get('tournament')}
            data-participants={bet.get('opponents').join(' - ')}
            data-expanded={isExpanded ? 'expanded' : 'collapsed'}
            data-market-name={bet.get('market')}
            data-market-id={bet.get('marketId')}
            data-match-id={bet.get('betEventId')}
            data-tournament-id={bet.get('tournament')}
            data-tournament-name={bet.get('tournamentId')}
        >
            <div
                data-atid="ticket-detail-bet"
                className={clsx('bet-header', {
                    won: !isDarkTheme && bet.get('status') === 'Won',
                    lost: !isDarkTheme && bet.get('status') === 'Lost',
                    closed: !isDarkTheme && bet.get('closeOdds')
                })}
                onClick={openScoreboard}
            >
                <TicketStateIcon bet={bet} isExpanded={isExpanded} />
                <PickHeader
                    bet={bet}
                    ticketId={ticketId}
                    createAnalysisBetIds={createAnalysisBetIds}
                    source={source}
                    currentUserTicket={currentUserTicket}
                />
            </div>
            {bet.get('betEventId') && !virtualStartedBet(bet) ? (
                <TicketScoreboardNg bet={bet} isExpanded={isExpanded} virtual={openVirtual} />
            ) : null}
            {cashinClosingModeActive ? (
                <CashinClosingModeExpandRow
                    bet={bet}
                    mobile
                    currentCashinOdds={currentCashinOdds}
                    pickState={pickState}
                    cashinClosingTransactionActive={newTransactionStarted}
                    onClick={handleCashinClosingClick}
                    onToggleDisabledPick={handleToggleDisabledPick}
                    ticketId={ticketId}
                />
            ) : null}
            {bet.get('closeTime') ? <CashinClosingModeClosedBet bet={bet} mobile /> : null}
        </div>
    );
}

BetNg.propTypes = {
    bet: PropTypes.object.isRequired
};

export default BetNg;
